import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FolderIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
// Temporary workaround - define MediaLibrary locally until export issue is resolved
interface MediaLibrary {
  id: number;
  name: string;
  slug: string;
  description?: string;
  type: 'product' | 'category' | 'general';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  media?: any[];
}

interface MediaLibraryFormData {
  name: string;
  slug: string;
  description?: string;
  type: 'product' | 'category' | 'general';
  is_active?: boolean;
}

interface MediaLibraryFilters {
  type?: 'product' | 'category' | 'general';
  is_active?: boolean;
  page?: number;
  limit?: number;
}
import { pimService } from '../../services/pimService';

interface MediaLibraryManagerProps {
  selectedLibraryId?: number;
  onSelectLibrary?: (library: MediaLibrary | null) => void;
  className?: string;
}

const MediaLibraryForm: React.FC<{
  library?: MediaLibrary;
  onSubmit: (data: MediaLibraryFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}> = ({ library, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState<MediaLibraryFormData>({
    name: library?.name || '',
    slug: library?.slug || '',
    description: library?.description || '',
    type: library?.type || 'general',
    is_active: library?.is_active ?? true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: !library ? generateSlug(name) : prev.slug // Only auto-generate for new libraries
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleNameChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          required
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Slug *
        </label>
        <input
          type="text"
          value={formData.slug}
          onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          required
          disabled={loading}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type
        </label>
        <select
          value={formData.type}
          onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          disabled={loading}
        >
          <option value="general">General</option>
          <option value="product">Product</option>
          <option value="category">Category</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          disabled={loading}
        />
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
          className="h-4 w-4 text-blue-600 rounded border-gray-300"
          disabled={loading}
        />
        <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
          Active
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
          disabled={loading}
        >
          {loading ? 'Saving...' : library ? 'Update Library' : 'Create Library'}
        </button>
      </div>
    </form>
  );
};

export const MediaLibraryManager: React.FC<MediaLibraryManagerProps> = ({
  selectedLibraryId,
  onSelectLibrary,
  className = ''
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingLibrary, setEditingLibrary] = useState<MediaLibrary | null>(null);
  const [filters, setFilters] = useState<MediaLibraryFilters>({});
  
  const queryClient = useQueryClient();

  // Fetch libraries
  const { data: librariesResponse, isLoading, error } = useQuery({
    queryKey: ['media-libraries', filters],
    queryFn: () => pimService.getMediaLibraries(filters),
    retry: 1,
    onError: (err) => {
      console.error('Failed to fetch media libraries:', err);
    }
  });

  const libraries = Array.isArray(librariesResponse?.libraries) 
    ? librariesResponse.libraries 
    : Array.isArray(librariesResponse?.data?.libraries) 
      ? librariesResponse.data.libraries 
      : Array.isArray(librariesResponse?.data) 
        ? librariesResponse.data 
        : [];

  // Create library mutation
  const createLibraryMutation = useMutation({
    mutationFn: (data: MediaLibraryFormData) => pimService.createMediaLibrary(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-libraries'] });
      setShowForm(false);
    }
  });

  // Update library mutation
  const updateLibraryMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<MediaLibraryFormData> }) =>
      pimService.updateMediaLibrary(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-libraries'] });
      setShowForm(false);
      setEditingLibrary(null);
    }
  });

  // Delete library mutation
  const deleteLibraryMutation = useMutation({
    mutationFn: (id: number) => pimService.deleteMediaLibrary(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-libraries'] });
      if (selectedLibraryId === editingLibrary?.id) {
        onSelectLibrary?.(null);
      }
    }
  });

  const handleCreateLibrary = (data: MediaLibraryFormData) => {
    createLibraryMutation.mutate(data);
  };

  const handleUpdateLibrary = (data: MediaLibraryFormData) => {
    if (editingLibrary) {
      updateLibraryMutation.mutate({ id: editingLibrary.id, data });
    }
  };

  const handleDeleteLibrary = (library: MediaLibrary) => {
    if (window.confirm(`Are you sure you want to delete the library "${library.name}"?`)) {
      deleteLibraryMutation.mutate(library.id);
    }
  };

  const startEdit = (library: MediaLibrary) => {
    setEditingLibrary(library);
    setShowForm(true);
  };

  const cancelEdit = () => {
    setShowForm(false);
    setEditingLibrary(null);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'product':
        return <PhotoIcon className="h-6 w-6" />;
      case 'category':
        return <DocumentIcon className="h-6 w-6" />;
      default:
        return <FolderIcon className="h-6 w-6" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'product':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'category':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'product':
        return 'Produkty';
      case 'category':
        return 'Kategorie';
      default:
        return 'Ogólne';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load media libraries</p>
          <p className="text-sm text-gray-500">{(error as Error)?.message || 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex flex-col space-y-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Biblioteki mediów</h2>
          <p className="text-sm text-gray-600">Organizuj pliki w tematycznych bibliotekach</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 shadow-sm transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nowa biblioteka
        </button>
      </div>

      {/* Filters */}
      {libraries.length > 0 && (
        <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Typ:</label>
            <select
              value={filters.type || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value as any }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Wszystkie typy</option>
              <option value="general">Ogólne</option>
              <option value="product">Produkty</option>
              <option value="category">Kategorie</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={filters.is_active?.toString() || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                is_active: e.target.value === '' ? undefined : e.target.value === 'true'
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="true">Aktywne</option>
              <option value="false">Nieaktywne</option>
            </select>
          </div>

          {(filters.type || filters.is_active !== undefined) && (
            <button
              onClick={() => setFilters({})}
              className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
            >
              Wyczyść filtry
            </button>
          )}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingLibrary ? 'Edit Library' : 'Create New Library'}
            </h3>
            <MediaLibraryForm
              library={editingLibrary || undefined}
              onSubmit={editingLibrary ? handleUpdateLibrary : handleCreateLibrary}
              onCancel={cancelEdit}
              loading={createLibraryMutation.isPending || updateLibraryMutation.isPending}
            />
          </div>
        </div>
      )}

      {/* Libraries List */}
      {libraries.length === 0 ? (
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FolderIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Brak bibliotek mediów</h3>
          <p className="text-gray-500 mb-4">Utwórz pierwszą bibliotekę aby organizować swoje pliki.</p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Utwórz pierwszą bibliotekę
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* All Media option */}
          <div 
            className={`
              p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md
              ${!selectedLibraryId 
                ? 'border-blue-500 bg-blue-50 shadow-md' 
                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }
            `}
            onClick={() => onSelectLibrary?.(null)}
          >
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className={`p-2 rounded-lg ${
                  !selectedLibraryId ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <PhotoIcon className={`h-6 w-6 ${
                    !selectedLibraryId ? 'text-blue-600' : 'text-gray-500'
                  }`} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900">Wszystkie media</h3>
                <p className="text-xs text-gray-500">Wyświetl wszystkie przesłane pliki</p>
                <div className="mt-1">
                  <span className="text-xs text-blue-600 font-medium">
                    {libraries.reduce((total, lib) => total + (lib.media?.length || 0), 0)} plików
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Library cards */}
          {libraries.map(library => (
            <div 
              key={library.id}
              className={`
                p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md group
                ${selectedLibraryId === library.id 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }
              `}
              onClick={() => onSelectLibrary?.(library)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    <div className={`p-2 rounded-lg ${
                      selectedLibraryId === library.id ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {getTypeIcon(library.type)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate mb-1">
                      {library.name}
                    </h3>
                    {library.description ? (
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2 leading-relaxed">
                        {library.description}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400 italic mt-1">
                        Brak opisu
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-md border ${getTypeColor(library.type)}`}>
                          {getTypeLabel(library.type)}
                        </span>
                        {!library.is_active && (
                          <span className="px-2 py-1 text-xs bg-red-100 text-red-700 border border-red-200 rounded-md">
                            Nieaktywne
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        {library.media?.length || 0} plików
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center space-y-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); startEdit(library); }}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Edytuj bibliotekę"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteLibrary(library); }}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Usuń bibliotekę"
                    disabled={deleteLibraryMutation.isPending}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibraryManager;